import React, { memo } from 'react'
import { Link } from 'transfix-ui/core'
import Skeleton from 'transfix-ui/core/Skeleton'
import Typography from 'transfix-ui/core/Typography'
import { makeStyles } from 'transfix-ui/core/styles'
import { brandColors } from 'transfix-ui/theme/colors'
import { Maybe } from 'types/graphqlTypes'
import ShipmentStopsPreview from '../ShipmentStopsPreview'
import ShipmentDetailsCardIcons from './components/ShipmentDetailsCardIcons'
import ShipmentDetailsCardMetrics from './components/ShipmentDetailsCardMetrics'
import { useShipmentDetailsCardInfoQuery } from './graphql/ShipmentDetailsCardInfo'

const useShipmentDetailsCardStyles = makeStyles(theme => ({
  card: {
    backgroundColor: brandColors.coolGray0,
    padding: `${theme.spacing(2)} ${theme.spacing(3)}`,
  },
  cardTop: {
    display: 'flex',
    justifyContent: 'space-between',
    margin: `0 0 ${theme.spacing(1)}`,
    '&.compact': {
      flexDirection: 'column',
      margin: `0 0 ${theme.spacing(1.5)}`,
    },
    '& .shipper': {
      margin: `${theme.spacing(0.5)} 0 0`,
    },
  },
  shipment: {
    display: 'flex',
    alignItems: 'center',
  },
}))

export interface IShipmentDetailsCardProps {
  shipmentId: Maybe<string>
  compact?: boolean
}

const ShipmentDetailsCard = memo<IShipmentDetailsCardProps>(({ compact = false, shipmentId }) => {
  const classes = useShipmentDetailsCardStyles()
  const { data, loading } = useShipmentDetailsCardInfoQuery({
    variables: {
      shipmentId: shipmentId || '',
    },
    skip: !shipmentId,
  })
  const { shipment } = data || {}
  const {
    calculatedMiles,
    characteristics,
    declaredValue,
    equipment,
    firstPickup,
    lastDelivery,
    shipperApplication,
    shipperRateType,
    stops,
    team,
    totalPalletCount,
    totalWeight,
  } = shipment || {}
  const { id: shipperId, name: shipperName } = shipperApplication || {}
  const { name: equipmentName, parentName: equipmentParentName } = equipment || {}

  return (
    <div className={classes.card}>
      <div className={`${compact ? 'compact' : ''} ${classes.cardTop}`}>
        <div className={classes.shipment}>
          <Link href={`${__ROOT__}/shipment-details/${shipmentId}`} target='_blank'>
            <Typography variant='subtitle2' color='primary' component='span'>
              #{shipmentId}
            </Typography>
          </Link>
          {!compact && characteristics && (
            <ShipmentDetailsCardIcons
              characteristics={characteristics}
              declaredValue={declaredValue}
              shipperRateType={shipperRateType}
              stops={stops}
            />
          )}
        </div>
        <div className='shipper'>
          {!loading && (
            <Link href={`${__ROOT__}/shippers/${shipperId}`} target='_blank'>
              <Typography variant='subtitle2' color='primary' component='span'>
                {shipperName}
              </Typography>
            </Link>
          )}
          {loading && <Skeleton variant='text' width={100} height={19} />}
        </div>
      </div>
      <div>
        <ShipmentStopsPreview
          loading={loading}
          firstPickup={firstPickup}
          lastDelivery={lastDelivery}
          stops={stops}
        />
        {!compact && (
          <ShipmentDetailsCardMetrics
            loading={loading}
            calculatedMiles={calculatedMiles}
            equipmentName={equipmentName}
            equipmentParentName={equipmentParentName}
            team={team || false}
            totalPalletCount={totalPalletCount}
            totalWeight={totalWeight}
          />
        )}
      </div>
    </div>
  )
})

export default ShipmentDetailsCard
